import * as React from "react";
import {
  Grid,
  Modal,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { formatToUsDate, getDateTime } from "../../../../../utils/DateHelper";
import { AuthenticationContext } from "../../../../../Context";
import { getHeaders } from "../../../../../utils/HeaderHelper";

interface Props {
  patientId: number | undefined;
  showPatientInformationHistory: boolean;
  closePatientInformationHistoryModal: () => void;
}

const PatientInformationHistory: React.FC<Props> = ({
  patientId,
  showPatientInformationHistory,
  closePatientInformationHistoryModal,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const [historyData, setHistoryData] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!showPatientInformationHistory || !patientId) return;
    setLoading(true);
    setError(null);
    fetch(`/data_fetching/patient_information_history/${patientId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (typeof result.error !== "undefined") {
          setError(result.error?.message || "Failed to load history");
          setHistoryData([]);
        } else {
          setHistoryData(result.patient_change_logs || []);
        }
      })
      .catch((err) => {
        setError(err?.message || "Failed to load history");
        setHistoryData([]);
      })
      .finally(() => setLoading(false));
  }, [showPatientInformationHistory, patientId, authenticationSetting.csrfToken]);

  return (
    <Modal
      open={showPatientInformationHistory}
      className={"modal-primary"}
      onClose={closePatientInformationHistoryModal}
    >
      <div className="paper" style={{ width: "950px", height: "90vh" }}>
        <div className="paperInner">
          <Grid container>
            <Grid item xs={12}>
              <div className={`main-header`} style={{ marginBottom: "0px" }}>
                <span>Patient Information History</span>
                <span
                  id="dismiss-button"
                  onClick={closePatientInformationHistoryModal}
                >
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </span>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div className="content" style={{ padding: "0 0 40px 0" }}>
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "200px",
                    }}
                  >
                    <CircularProgress />
                  </div>
                ) : error ? (
                  <div style={{ padding: "24px", color: "#d32f2f" }}>
                    {error}
                  </div>
                ) : (
                  <TableContainer sx={{ height: "75vh" }}>
                    <Table stickyHeader>
                      <TableHead
                        sx={{
                          boxShadow: "0px 4px 4px 0px #00000040",
                          backgroundColor: "#fff",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <TableRow
                          sx={{
                            "& td, & th": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell
                            align="left"
                            sx={{ paddingLeft: "40px", width: "150px" }}
                          >
                            Date
                          </TableCell>
                          <TableCell align="left" sx={{ width: "180px" }}>
                            Actor
                          </TableCell>
                          <TableCell align="left" sx={{ width: "180px" }}>
                            Organization
                          </TableCell>
                          <TableCell align="left" sx={{ paddingRight: "40px" }}>
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyData?.map((changeLog) => (
                          <TableRow
                            key={changeLog.id}
                            sx={{ "& td, & th": { borderBottom: "none" } }}
                          >
                            <TableCell align="left" sx={{ paddingLeft: "40px" }}>
                              {`${formatToUsDate(changeLog.created_at)} ${getDateTime(changeLog.created_at)}`}
                            </TableCell>
                            <TableCell align="left" sx={{ width: "180px" }}>
                              {changeLog.actor}
                            </TableCell>
                            <TableCell align="left" sx={{ width: "180px" }}>
                              {changeLog.organization}
                            </TableCell>
                            <TableCell align="left" sx={{ paddingRight: "40px" }}>
                              {changeLog.formatted_action}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    </Modal>
  );
};

export default PatientInformationHistory;
